import React, { useState, useEffect } from "react";
import { BiDownload, BiFilter, BiRefresh, BiUser } from "react-icons/bi";
import { useCommissionReport } from "../../../hooks";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatDisplayDate,
} from "../../../utils/formatters";
import {
  ReportFilters,
  ReportTabs,
  KPICard,
  DataTable,
  ExportModal,
} from "./shared";
import { toast } from "react-toastify";
import { exportReport } from "../../../utils/reportExport";
import { buildCommissionsExport } from "./reportExports";
import { formatSalonDate } from "../../../utils/salonTime";

const CommissionsReport = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const [showFilters, setShowFilters] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(null);
  const [filters, setFilters] = useState({
    period: "month",
    startDate: null,
    endDate: null,
    groupBy: "employee",
  });

  const { loading, error, summary, getSummary, getDetails, getEmployeeReport } =
    useCommissionReport();

  const [commissionSummary, setCommissionSummary] = useState(null);
  const [details, setDetails] = useState({ data: [], pagination: {} });
  const [employeeReport, setEmployeeReport] = useState(null);

  useEffect(() => {
    loadTabData();
  }, [activeTab, filters]);

  const loadTabData = async () => {
    try {
      switch (activeTab) {
        case "summary":
          const summaryData = await getSummary(filters);
          setCommissionSummary(summaryData);
          break;
        case "details":
          const detailsResult = await getDetails({ ...filters, page: 1 });
          setDetails(detailsResult);
          break;
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
    }
  };

  const handlePageChange = async (page) => {
    const result = await getDetails({ ...filters, page });
    setDetails(result);
  };

  const handleViewEmployee = async (employeeId) => {
    try {
      const report = await getEmployeeReport(employeeId, filters);
      setEmployeeReport(report);
      setShowEmployeeDetails(true);
    } catch (error) {
      console.error("Erreur chargement rapport employé:", error);
    }
  };

  const handleExport = async (format, options) => {
    try {
      const config = await buildCommissionsExport(filters, activeTab);
      await exportReport(config, format, options);
      toast.success("Export du rapport terminé");
    } catch (error) {
      console.error("Erreur export:", error);
      toast.error(error?.message || "Erreur lors de l'export du rapport");
      // Laisser la fenêtre d'export ouverte pour permettre un nouvel essai
      throw error;
    }
  };

  const tabs = [
    { id: "summary", label: "Résumé" },
    { id: "details", label: "Détail", count: details.pagination?.total },
  ];

  const summaryColumns = [
    {
      key: "employeeName",
      label: "Employé",
      render: (row) => (
        <button
          onClick={() => handleViewEmployee(row.employeeId)}
          className="text-secondary-600 hover:text-secondary-800 font-medium flex items-center gap-2"
        >
          <BiUser size={16} />
          {row.employeeName}
        </button>
      ),
    },
    {
      key: "appointmentsCount",
      label: "RDV",
      render: (row) => formatNumber(row.appointmentsCount),
    },
    {
      key: "revenue",
      label: "CA",
      render: (row) => formatCurrency(row.revenue),
    },
    {
      key: "commission",
      label: "Commission",
      render: (row) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(row.commission)}
        </span>
      ),
    },
    {
      key: "commissionRate",
      label: "Taux moyen",
      render: (row) => formatPercentage(row.commissionRate),
    },
  ];

  const detailsColumns = [
    {
      key: "appointmentCode",
      label: "RDV",
      render: (row) => <span className="font-mono">{row.appointmentCode}</span>,
    },
    {
      key: "appointmentDate",
      label: "Date",
      render: (row) => formatSalonDate(row.appointmentDate),
    },
    {
      key: "employeeName",
      label: "Employé",
      render: (row) => (
        <button
          onClick={() => handleViewEmployee(row.employeeId)}
          className="text-secondary-600 hover:text-secondary-800"
        >
          {row.employeeName}
        </button>
      ),
    },
    { key: "serviceName", label: "Service" },
    {
      key: "revenue",
      label: "CA",
      render: (row) => formatCurrency(row.revenue),
    },
    {
      key: "commissionRate",
      label: "Taux",
      render: (row) => formatPercentage(row.commissionRate),
    },
    {
      key: "commissionAmount",
      label: "Commission",
      render: (row) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(row.commissionAmount)}
        </span>
      ),
    },
    {
      key: "status",
      label: "Statut",
      render: (row) => {
        const colors = {
          pending: "bg-yellow-100 text-yellow-800",
          paid: "bg-green-100 text-green-800",
          cancelled: "bg-red-100 text-red-800",
        };
        const labels = {
          pending: "En attente",
          paid: "Payé",
          cancelled: "Annulé",
        };
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs ${colors[row.status]}`}
          >
            {labels[row.status]}
          </span>
        );
      },
    },
  ];

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        Une erreur est survenue : {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-poppins font-semibold text-[20px] text-title">
          Rapport des commissions
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiFilter size={18} />
            Filtres
          </button>
          <button
            onClick={() => setShowExport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors"
          >
            <BiDownload size={18} />
            Exporter
          </button>
          <button
            onClick={loadTabData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiRefresh size={18} />
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Onglets */}
      <ReportTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Contenu */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
        </div>
      ) : (
        <>
          {activeTab === "summary" && commissionSummary && (
            <div className="space-y-6">
              {/* KPIs globaux */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                  title="Total commissions"
                  value={formatCurrency(
                    commissionSummary.overview.totalCommissions,
                  )}
                  icon="💰"
                  color="green"
                />
                <KPICard
                  title="CA total"
                  value={formatCurrency(
                    commissionSummary.overview.totalRevenue,
                  )}
                  icon="📊"
                  color="blue"
                />
                <KPICard
                  title="Taux moyen"
                  value={formatPercentage(
                    commissionSummary.overview.averageCommissionRate,
                  )}
                  icon="⚖️"
                  color="purple"
                />
                <KPICard
                  title="En attente"
                  value={formatCurrency(
                    commissionSummary.overview.pendingCommissions,
                  )}
                  icon="⏳"
                  color="yellow"
                />
              </div>

              {/* Répartition par employé */}
              {commissionSummary.byEmployee && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-poppins font-semibold text-[16px] mb-4">
                    Commissions par employé
                  </h3>

                  {/* Graphique en barres */}
                  <div className="space-y-4 mb-6">
                    {commissionSummary.byEmployee.map((emp) => {
                      const percentage =
                        (emp.commission /
                          commissionSummary.overview.totalCommissions) *
                        100;
                      return (
                        <div key={emp.employeeId} className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="font-nunito text-[14px] font-medium">
                              {emp.employeeName}
                            </span>
                            <span className="font-nunito text-[14px] text-green-600 font-semibold">
                              {formatCurrency(emp.commission)}
                            </span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>
                              {formatNumber(emp.appointmentsCount)} RDV
                            </span>
                            <span>{formatCurrency(emp.revenue)} CA</span>
                            <span>{formatPercentage(emp.commissionRate)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Tableau récapitulatif */}
                  <DataTable
                    columns={summaryColumns}
                    data={commissionSummary.byEmployee}
                  />
                </div>
              )}

              {/* Top services */}
              {commissionSummary.byService && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-poppins font-semibold text-[16px] mb-4">
                    Commissions par service
                  </h3>
                  <div className="space-y-3">
                    {commissionSummary.byService.slice(0, 5).map((service) => (
                      <div
                        key={service.serviceId}
                        className="flex items-center justify-between"
                      >
                        <span className="font-nunito text-[14px]">
                          {service.serviceName}
                        </span>
                        <div className="flex items-center gap-4">
                          <span className="font-nunito text-[14px]">
                            {formatNumber(service.count)} prestations
                          </span>
                          <span className="font-nunito text-[14px]">
                            {formatCurrency(service.revenue)}
                          </span>
                          <span className="font-nunito text-[14px] font-semibold text-green-600">
                            {formatCurrency(service.commission)}
                          </span>
                          <span className="font-nunito text-[12px] text-gray-500">
                            ({formatPercentage(service.averageRate)})
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Période */}
              <div className="text-sm text-gray-500 text-right">
                Période du {formatDisplayDate(commissionSummary.period.start)}{" "}
                au {formatDisplayDate(commissionSummary.period.end)}
              </div>
            </div>
          )}

          {activeTab === "details" && (
            <DataTable
              columns={detailsColumns}
              data={details.data}
              pagination={details.pagination}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Modal détails employé */}
      {showEmployeeDetails && employeeReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-poppins font-semibold text-[18px]">
                Rapport de {employeeReport.employeeName}
              </h3>
              <button
                onClick={() => setShowEmployeeDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Infos employé */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">
                      {employeeReport.employeeEmail}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Téléphone</p>
                    <p className="font-medium">
                      {employeeReport.employeePhone}
                    </p>
                  </div>
                </div>
              </div>

              {/* KPIs employé */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard
                  title="Total commissions"
                  value={formatCurrency(employeeReport.periodCommissions.total)}
                  icon="💰"
                  color="green"
                />
                <KPICard
                  title="Payé"
                  value={formatCurrency(employeeReport.periodCommissions.paid)}
                  icon="✅"
                  color="blue"
                />
                <KPICard
                  title="En attente"
                  value={formatCurrency(
                    employeeReport.periodCommissions.pending,
                  )}
                  icon="⏳"
                  color="yellow"
                />
              </div>

              {/* Services */}
              {employeeReport.services.length > 0 && (
                <div>
                  <h4 className="font-poppins font-semibold text-[16px] mb-3">
                    Commissions par service
                  </h4>
                  <div className="space-y-2">
                    {employeeReport.services.map((service) => (
                      <div
                        key={service.serviceId}
                        className="flex justify-between items-center p-2 bg-gray-50 rounded"
                      >
                        <span>{service.serviceName}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-600">
                            {formatNumber(service.count)} prestations
                          </span>
                          <span className="font-semibold text-green-600">
                            {formatCurrency(service.commission)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Liste des rendez-vous */}
              {employeeReport.appointments.length > 0 && (
                <div>
                  <h4 className="font-poppins font-semibold text-[16px] mb-3">
                    Détail des rendez-vous
                  </h4>
                  <DataTable
                    columns={[
                      { key: "code", label: "Code" },
                      {
                        key: "date",
                        label: "Date",
                        render: (row) => formatDisplayDate(row.date),
                      },
                      { key: "service", label: "Service" },
                      {
                        key: "commission",
                        label: "Commission",
                        render: (row) => formatCurrency(row.commission),
                      },
                      { key: "status", label: "Statut" },
                    ]}
                    data={employeeReport.appointments}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal d'export */}
      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          onExport={handleExport}
        />
      )}
    </div>
  );
};

export default CommissionsReport;
